import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  FILE_FREQUENCY_LABELS,
  FILE_STATUS_LABELS,
  FILE_TYPE_LABELS,
  FileFrequencyEnum,
  FileTrackerStatus,
  FileTypeEnum,
  IFileImportFileTracker,
} from './types';

interface IFileImportListTableProps {
  files: IFileImportFileTracker[];
}

const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  [FileTrackerStatus.Pending]: 'default',
  [FileTrackerStatus.Validated]: 'info',
  [FileTrackerStatus.Completed]: 'success',
  [FileTrackerStatus.Failed]: 'error',
};

const formatFileType = (fileType: string): string => {
  const key = Object.entries(FileTypeEnum).find(([, v]) => v.toLowerCase() === fileType)?.[0];
  return key ? FILE_TYPE_LABELS[FileTypeEnum[key as keyof typeof FileTypeEnum]] : fileType;
};

const formatFrequency = (frequency: string): string => {
  const key = Object.entries(FileFrequencyEnum).find(([, v]) => v.toLowerCase() === frequency)?.[0];
  return key
    ? FILE_FREQUENCY_LABELS[FileFrequencyEnum[key as keyof typeof FileFrequencyEnum]]
    : frequency;
};

const formatStatus = (status: string): string =>
  FILE_STATUS_LABELS[status as FileTrackerStatus] ?? status;

const formatDate = (dateStr: string): string => {
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
};

const FileImportListTable = ({ files }: IFileImportListTableProps): JSX.Element => {
  return (
    <TableContainer component={Paper}>
      <Table aria-label="file import list table">
        <TableHead>
          <TableRow>
            <TableCell>File Name</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Frequency</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Total Rows</TableCell>
            <TableCell align="right">Success</TableCell>
            <TableCell align="right">Errors</TableCell>
            <TableCell>Uploaded</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {files.map(file => (
            <TableRow key={file.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
              <TableCell component="th" scope="row" sx={{ wordBreak: 'break-all', maxWidth: 280 }}>
                {file.file_name}
              </TableCell>
              <TableCell>{formatFileType(file.file_type)}</TableCell>
              <TableCell>{formatFrequency(file.frequency)}</TableCell>
              <TableCell>
                <Chip
                  label={formatStatus(file.status)}
                  color={STATUS_COLOR[file.status] ?? 'default'}
                  size="small"
                />
              </TableCell>
              <TableCell align="right">{file.total_rows ?? '—'}</TableCell>
              <TableCell align="right">{file.success_count ?? '—'}</TableCell>
              <TableCell align="right">{file.error_count ?? '—'}</TableCell>
              <TableCell>{formatDate(file.created_ts)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FileImportListTable;
